import React, { useEffect, useState } from 'react'

export interface PortInfo {
  port:     string
  protocol: string
  banner:   string | null
  severity: string | null
  assetId?: string | number
}

interface Props {
  portInfo:       PortInfo
  triggerElement: HTMLElement
}

export const PortSpeechBubble: React.FC<Props> = ({ portInfo, triggerElement }) => {
  const [position, setPosition] = useState({ top: 0, left: 0, arrowLeft: 0, showAbove: false })

  useEffect(() => {
    const updatePosition = () => {
      if (!triggerElement) return
      const rect = triggerElement.getBoundingClientRect()
      const bubbleWidth = 320
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let left = rect.left + (rect.width / 2) - (bubbleWidth / 2)
      let top  = rect.bottom + 8
      let showAbove = false

      if (left + bubbleWidth > viewportWidth - 16) left = viewportWidth - bubbleWidth - 16
      if (left < 16) left = 16

      const bubbleHeight = 200
      if (top + bubbleHeight > viewportHeight - 16) {
        top = rect.top - bubbleHeight - 8
        showAbove = true
      }

      const arrowLeft = rect.left + (rect.width / 2) - left

      setPosition({
        top, left,
        arrowLeft: Math.max(12, Math.min(arrowLeft, bubbleWidth - 20)),
        showAbove,
      })
    }

    const rafId = requestAnimationFrame(updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [triggerElement])

  const getSeverityColor = (severity: string | null): string => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL':
        return 'bg-red-500 text-white border-red-600 dark:bg-red-900 dark:text-red-100 dark:border-red-700'
      case 'HIGH':
        return 'bg-orange-500 text-white border-orange-600 dark:bg-orange-900 dark:text-orange-100 dark:border-orange-700'
      case 'MEDIUM':
        return 'bg-yellow-500 text-white border-yellow-600 dark:bg-yellow-900 dark:text-yellow-100 dark:border-yellow-700'
      case 'LOW':
        return 'bg-blue-500 text-white border-blue-600 dark:bg-blue-900 dark:text-blue-100 dark:border-blue-700'
      case 'INFO':
        return 'bg-slate-500 text-white border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600'
      default:
        return 'bg-slate-400 text-white border-slate-500 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600'
    }
  }

  return (
    <div
      data-port-bubble
      className="fixed z-50 pointer-events-auto"
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
    >
      <div className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-lg shadow-2xl p-3 max-w-sm border-2 border-slate-300 dark:border-slate-600">
        <div
          className="absolute w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-white dark:border-b-slate-800"
          style={{
            [position.showAbove ? 'bottom' : 'top']: '-8px',
            left: `${position.arrowLeft}px`,
            transform: position.showAbove ? 'rotate(180deg)' : 'none',
            filter: 'drop-shadow(0 -2px 2px rgba(0,0,0,0.1))',
          }}
        />

        <div className="space-y-2 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold text-sm text-slate-900 dark:text-white">
              {portInfo.port}/{portInfo.protocol}
            </span>
            {portInfo.severity && (
              <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${getSeverityColor(portInfo.severity)}`}>
                {portInfo.severity}
              </span>
            )}
          </div>

          {portInfo.banner && (
            <div className="pt-2 border-t border-slate-300 dark:border-slate-600">
              <div className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Banner:</div>
              <div className="font-mono text-xs whitespace-pre-wrap break-words max-h-48 overflow-y-auto text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-700 p-2 rounded">
                {portInfo.banner}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
